import { useCallback, useEffect, useMemo, useState } from "react";
import useCompany from "$hooks/useCompany";
import { CompanyType, DeliveryType } from "$types";
import { CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

const useCompanyOptions = (
  deliveryType?: DeliveryType,
  carrierCompanyId?: string
) => {
  const { isAgency, isCarrier } = useCompany();
  const [companiesAgencyCarrier, setCompaniesAgencyCarrier] = useState(null);
  const [relayAgencyOptions, setRelayAgencyOptions] = useState(null);

  // get companies for agencyOptions and carrierOptions
  const fecthCompaniesAgencyCarrier = useCallback(async () => {
    const filters = {
      ...(deliveryType && { deliveryType }),
      $inCompanyTypes: [CompanyType.AGENCY, CompanyType.CARRIER],
      page: 1,
      rowsPerPage: 10000,
    };

    const companies = await CompanyServices.getCompanies(filters);
    setCompaniesAgencyCarrier(companies.data);

    await queryClient.setQueryData(
      [CacheInvalidationAction.GET_COMPANIES, filters],
      companies.data
    );
  }, [setCompaniesAgencyCarrier, deliveryType]);

  useEffect(() => {
    fecthCompaniesAgencyCarrier();
  }, [fecthCompaniesAgencyCarrier]);

  const agencyOptions = useMemo(() => {
    return (companiesAgencyCarrier || []).filter((c) => {
      if (!isAgency(c.companyTypes) || c.agencyOf !== carrierCompanyId) {
        return false;
      }
      return true;
    });
  }, [carrierCompanyId, isAgency, companiesAgencyCarrier]);

  const carrierOptions = useMemo(() => {
    return (companiesAgencyCarrier || []).filter((c) => {
      if (!isCarrier(c.companyTypes)) {
        return false;
      }
      return true;
    });
  }, [isCarrier, companiesAgencyCarrier]);

  // get companies for relayAgencyOptions
  const fecthCompaniesRelayAgency = useCallback(async () => {
    const filters = {
      companyTypes: CompanyType.AGENCY,
      relayPoint: true,
      page: 1,
      rowsPerPage: 10000,
    };

    const companies = await CompanyServices.getCompanies(filters);
    setRelayAgencyOptions(companies.data);
    await queryClient.setQueryData(
      [CacheInvalidationAction.GET_COMPANIES, filters],
      companies.data
    );
  }, [setRelayAgencyOptions]);

  useEffect(() => {
    fecthCompaniesRelayAgency();
  }, [fecthCompaniesRelayAgency]);

  const refetchCompanies = useCallback(async () => {
    await fecthCompaniesAgencyCarrier();
    await fecthCompaniesRelayAgency();
  }, [fecthCompaniesAgencyCarrier, fecthCompaniesRelayAgency]);

  return {
    agencyOptions,
    carrierOptions,
    relayAgencyOptions,
    refetchCompanies,
  };
};

export default useCompanyOptions;
